import { Box, Button, Typography, styled } from "@mui/material"
import type React from "react"
import { useEffect, useRef, useState } from "react"
import { useSearchParams } from "../../hooks/useSearchParams"
import { useContainerQueryUp } from "../../hooks/useContainerQuery"
import { extraColors } from "../../utils/theme"
import { useChat } from "./ChatContext"
import { ChatInput } from "./ChatInput"
import { ChatModelPicker } from "./ChatModelPicker"
import { MessageList } from "./MessageList"
import type { NewChatMessage } from "./useChatSession"

const ScrollContainer = styled(Box)(() => ({
  flex: 1,
  overflowY: "auto",
  padding: "12px 0",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: extraColors.translucentWhite,
    borderRadius: "3px",
  },
}))

const useNewChatMessage = () => {
  const { sendMessage, isLoadingModels } = useChat()
  const { newChatMessage, setSearchParams } = useSearchParams()

  const [pendingMessage] = useState<NewChatMessage | null>(() => {
    if (!newChatMessage) return null
    try {
      return JSON.parse(newChatMessage) as NewChatMessage
    } catch {
      return null
    }
  })

  const hasSentRef = useRef(false)

  useEffect(() => {
    if (hasSentRef.current || !pendingMessage || isLoadingModels) return
    hasSentRef.current = true
    setSearchParams({ newChatMessage: undefined })
    void sendMessage(pendingMessage)
  }, [pendingMessage, isLoadingModels, sendMessage, setSearchParams])
}

export const ChatContainer: React.FC = () => {
  const {
    messages,
    sendMessage,
    stopStreaming,
    isStreaming,
    isLoadingModels,
    hasModelsError,
    retryModels,
    selectedModel,
  } = useChat()
  const scrollRef = useRef<HTMLDivElement>(null)
  const { contentRef, isAboveBreakpoint } = useContainerQueryUp({
    containerWidth: 600,
  })
  const isAutoScrollEnabled = useRef(true)

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement
    const isAtBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight < 150
    isAutoScrollEnabled.current = isAtBottom
  }

  useNewChatMessage()

  useEffect(() => {
    if (scrollRef.current && isAutoScrollEnabled.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages])

  return (
    <Box
      ref={contentRef}
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: "transparent",
        height: "100%",
      }}
    >
      <ScrollContainer ref={scrollRef} onScroll={handleScroll}>
        <Box
          sx={{
            maxWidth: "800px",
            width: "100%",
            mx: "auto",
            px: isAboveBreakpoint ? 4 : 2,
          }}
        >
          <MessageList messages={messages} />
        </Box>
      </ScrollContainer>

      <Box
        sx={{
          py: 3,
          px: isAboveBreakpoint ? 4 : 2,
          borderTop: `1px solid ${extraColors.translucentWhite}`,
          bgcolor: "transparent",
        }}
      >
        <Box
          sx={{ maxWidth: "800px", width: "100%", display: "grid", gap: 1.5 }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1.5,
            }}
          >
            <Typography variant="small1" sx={{ color: extraColors.medium }}>
              Model
            </Typography>
            {hasModelsError && (
              <Button
                size="small"
                variant="text"
                onClick={retryModels}
                sx={{
                  minWidth: 0,
                  px: 1,
                  color: extraColors.status.navyLight,
                }}
              >
                Retry
              </Button>
            )}
            <ChatModelPicker />
          </Box>
          <ChatInput
            onSend={query => void sendMessage({ query })}
            isStreaming={isStreaming}
            onStop={stopStreaming}
            disabled={
              isStreaming || isLoadingModels || hasModelsError || !selectedModel
            }
          />
        </Box>
      </Box>
    </Box>
  )
}
